// src/Pages/AllOffers.jsx  (or wherever your pages live)
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import { FiSearch, FiX, FiMapPin } from "react-icons/fi";
import { MdOutlineLocalOffer } from "react-icons/md";
import axios from "../../Utils/axiosInstance";
import PayToViewOfferModal from "../../Components/UserSideComponents/Home/Components/PayToViewOfferModal";

// ── helpers ───────────────────────────────────────────────────────────────────
const getExpired = (offer) =>
  offer?.validityPeriod?.isExpired ||
  (offer?.validityPeriod?.expiryDate &&
    new Date(offer.validityPeriod.expiryDate) < new Date());

const fmtKg = (qty) => {
  if (!qty?.weightInNumber) return "N/A";
  const unit = qty.weightIn ? ` ${qty.weightIn}` : "";
  return `${qty.weightInNumber.toLocaleString()}${unit}`;
};

// ── Countdown hook ────────────────────────────────────────────────────────────
const useCountdown = (expiryDate, isUrgent) => {
  const [timeLeft, setTimeLeft] = useState(null);
  useEffect(() => {
    if (!isUrgent || !expiryDate) return;
    const calc = () => {
      const diff = new Date(expiryDate) - new Date();
      if (diff <= 0) return setTimeLeft({ d: "00", h: "00", m: "00", s: "00" });
      setTimeLeft({
        d: String(Math.floor(diff / 86400000)).padStart(2, "0"),
        h: String(Math.floor((diff % 86400000) / 3600000)).padStart(2, "0"),
        m: String(Math.floor((diff % 3600000) / 60000)).padStart(2, "0"),
        s: String(Math.floor((diff % 60000) / 1000)).padStart(2, "0"),
      });
    };
    calc();
    const t = setInterval(calc, 1000);
    return () => clearInterval(t);
  }, [isUrgent, expiryDate]);
  return timeLeft;
};

// ── Single offer card (grid version) ─────────────────────────────────────────
const OfferGridCard = ({ item, onOfferClick }) => {
  const navigate = useNavigate();
  const { offer, productVariety } = item;
  const daysLeft = offer?.daysLeft ?? 0;
  const isUrgent = daysLeft <= 3;
  const expired = getExpired(offer);
  const timeLeft = useCountdown(offer?.validityPeriod?.expiryDate, isUrgent && !expired);

  const price = Math.round(offer?.pricing?.amount ?? 0);
  const rawPriceType = offer?.pricing?.priceType ?? "Per Kg";
  const priceType = (rawPriceType || "kg").replace(/per/gi, "").replace(/price/gi, "").trim().toLowerCase() || "kg";
  const varietyName = productVariety?.varietyName ?? "Product";
  const grade = productVariety?.grade ?? "";
  const offerNumber = offer?.offerUniqueNumber;
  const gradeLabel = grade
    ? /^grade\b/i.test(grade.trim())
      ? grade.trim().toUpperCase()
      : `GRADE ${grade}`
    : "";
  const uploadedImg = offer?.media?.images?.find(Boolean) || offer?.media?.images?.[0];
  const img = offer?.bannerImg || uploadedImg || productVariety?.img || "";
  const isLoggedIn = Boolean(localStorage.getItem("qqc_userToken"));
  const locationStr =
    offer?.location?.formatted ||
    [offer?.location?.city, offer?.location?.state].filter(Boolean).join(", ") ||
    "";

  const handleClick = () => {
    if (expired) {
      toast("🕐 This offer has expired. Stay tuned — it'll be back soon!", {
        icon: "⏳",
        style: { borderRadius: "10px", background: "#3b2012", color: "#fff" },
      });
      return;
    }
    if (!isLoggedIn) {
      navigate("/login");
      return;
    }
    // ── Pass to parent handler instead of direct navigation
    if (onOfferClick) {
      onOfferClick(item);
    }
  };

  return (
    <div
      onClick={handleClick}
      className={`
        relative rounded-2xl overflow-hidden cursor-pointer
        flex flex-col
        border border-gray-100
        shadow-sm hover:shadow-md
        transition-all duration-200 active:scale-[0.98]
        ${expired ? "opacity-60 grayscale" : ""}
      `}
      style={{ background: "#f7fbe8" }}
    >
      {/* Image */}
      <div className="relative h-36 sm:h-40 w-full overflow-hidden bg-[#eef5d6]">
        {img ? (
          <img src={img} alt={varietyName} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <MdOutlineLocalOffer size={40} className="text-gray-300" />
          </div>
        )}

        {/* Expired badge */}
        {expired && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/30">
            <span className="bg-white/90 text-gray-700 text-xs font-bold px-3 py-1 rounded-full">
              Expired
            </span>
          </div>
        )}

        {/* Urgency badge */}
        {!expired && isUrgent && (
          <div className="absolute top-2 left-2">
            <span className="flex items-center gap-1 bg-red-600 text-white text-[9px] font-bold px-2 py-0.5 rounded-full">
              <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
              ENDING SOON
            </span>
          </div>
        )}
      </div>

      {/* Body */}
      <div className="flex flex-col flex-1 p-3 gap-2">
        {/* Name + offer number / grade */}
        <div>
          <p className="text-sm font-bold text-[#3b2012] leading-snug line-clamp-1">
            {varietyName}
          </p>
          {offerNumber ? (
            <span className="inline-block mt-0.5 text-[11px] font-bold text-primaryColor tracking-wide">
              {offerNumber} {gradeLabel ? <span className="text-gray-600 font-semibold">({gradeLabel})</span> : null}
            </span>
          ) : gradeLabel ? (
            <span className="inline-block mt-0.5 text-[10px] font-semibold text-gray-500 tracking-wide">
              {gradeLabel}
            </span>
          ) : null}
        </div>

        {/* Quantity */}
        <div className="flex items-center gap-1 text-[10px] text-gray-500">
          <svg className="w-3 h-3 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10" />
          </svg>
          <span>Offer Quantity: <span className="font-semibold text-gray-700">{fmtKg(offer?.quantity)}</span></span>
        </div>

        {/* Location (City & State) */}
        {locationStr ? (
          <div className="flex items-center gap-1 text-[10px] font-semibold text-[#3b2012]/85 truncate">
            <FiMapPin className="text-primaryColor w-3 h-3 flex-shrink-0" />
            <span className="truncate">{locationStr}</span>
          </div>
        ) : null}

        {/* Timer OR days left */}
        <div className="min-h-[28px] flex items-center">
          {expired ? (
            <span className="text-[10px] text-gray-400 font-medium">Coming Back Soon</span>
          ) : isUrgent && timeLeft ? (
            <div className="flex items-center gap-1 flex-wrap">
              <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse flex-shrink-0" />
              {[
                { val: timeLeft.d, label: "D" },
                { val: timeLeft.h, label: "H" },
                { val: timeLeft.m, label: "M" },
                { val: timeLeft.s, label: "S" },
              ].map(({ val, label }, i, arr) => (
                <React.Fragment key={label}>
                  <div className="flex flex-col items-center justify-center rounded px-1 py-0.5 min-w-[22px]"
                    style={{ background: "rgba(192,57,43,0.10)", border: "1px solid rgba(192,57,43,0.20)" }}>
                    <span className="text-[11px] font-extrabold tabular-nums leading-none" style={{ color: "#c0392b" }}>{val}</span>
                    <span className="text-[7px] font-bold uppercase leading-none mt-[1px]" style={{ color: "rgba(192,57,43,0.65)" }}>{label}</span>
                  </div>
                  {i < arr.length - 1 && (
                    <span className="text-[10px] font-black mb-1.5" style={{ color: "rgba(192,57,43,0.40)" }}>:</span>
                  )}
                </React.Fragment>
              ))}
            </div>
          ) : (
            <span className="text-[10px] font-semibold text-gray-600">
              {String(daysLeft).padStart(2, "0")} Days Left
            </span>
          )}
        </div>

        {/* Divider */}
        <div className="border-t border-gray-200/80" />

        {/* Price + CTA */}
        <div className="flex items-center justify-between gap-2 pt-0.5">
          <div className="flex items-baseline gap-0.5 min-w-0">
            <span className="text-base font-extrabold text-[#3b2012] whitespace-nowrap">
              ₹{price}
            </span>
            <span className="text-[9px] text-gray-500 font-medium whitespace-nowrap">
              /{priceType}
            </span>
          </div>
          <button
            className={`
      text-[11px] font-bold px-3 py-1.5 rounded-lg transition-all flex-shrink-0 whitespace-nowrap
      ${expired
                ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                : "bg-[#3b2012] text-white hover:bg-[#5a3020] active:scale-95"
              }
    `}
          >
            {expired ? "Expired" : "Order Now"}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Skeleton card ─────────────────────────────────────────────────────────────
const SkeletonCard = () => (
  <div className="rounded-2xl overflow-hidden border border-gray-100 animate-pulse">
    <div className="h-36 sm:h-40 bg-gray-200" />
    <div className="p-3 space-y-2">
      <div className="h-4 bg-gray-200 rounded w-3/4" />
      <div className="h-3 bg-gray-100 rounded w-1/2" />
      <div className="h-3 bg-gray-100 rounded w-2/3" />
      <div className="h-8 bg-gray-200 rounded-lg mt-2" />
    </div>
  </div>
);

// ── Empty state ───────────────────────────────────────────────────────────────
const EmptyState = ({ onClear }) => (
  <div className="col-span-full flex flex-col items-center justify-center py-20 gap-4">
    <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center">
      <MdOutlineLocalOffer size={32} className="text-gray-400" />
    </div>
    <p className="text-gray-500 font-medium text-sm">No offers found</p>
    <button onClick={onClear} className="text-xs text-primaryColor underline underline-offset-2">
      Clear filters
    </button>
  </div>
);

// ── Main page ─────────────────────────────────────────────────────────────────
const AllOffers = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const token = localStorage.getItem("qqc_userToken");
  const isLoggedIn = Boolean(token);
  const storedUser = (() => {
    try { return JSON.parse(localStorage.getItem("qqc_userData") || "{}"); }
    catch { return {}; }
  })();
  const buyerId = storedUser?._id ?? storedUser?.id ?? null;

  // ── State ──────────────────────────────────────────────────────────────────
  const initialTab = !isLoggedIn
    ? "hot"
    : searchParams.get("type") === "recommended"
      ? "recommended"
      : "hot";
  const [activeTab, setActiveTab] = useState(initialTab);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [hotDeals, setHotDeals] = useState([]);
  const [recommendedDeals, setRecommendedDeals] = useState([]);
  // ── Pay-to-view gate state ─────────────────────────────────────────────
  const [wallet, setWallet] = useState(null);
  const [payModal, setPayModal] = useState({ show: false, offer: null });
  const [checkingOfferId, setCheckingOfferId] = useState(null);
  const [showRecharge, setShowRecharge] = useState(false);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totals, setTotals] = useState({ hot: 0, recommended: 0 });
  const PAGE_SIZE = 12;

  // ── Debounce search ────────────────────────────────────────────────────────
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 350);
    return () => clearTimeout(t);
  }, [search]);

  // ── Reset page on tab/search change ───────────────────────────────────────
  useEffect(() => { setPage(1); }, [activeTab, debouncedSearch]);

  // ── Fetch ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    const fetchOffers = async () => {
      try {
        setLoading(true);
        const params = new URLSearchParams({ search: debouncedSearch });

        const hotDealsRes = await axios.get(`/offer/get-hot-deals?${params}`);

        if (hotDealsRes.data.success) {
          setHotDeals(hotDealsRes.data.data.Hot_Deals ?? []);
          setTotals((prev) => ({ ...prev, hot: hotDealsRes.data.data.total ?? 0 }));
        }

        if (isLoggedIn && buyerId) {
          const recommendedRes = await axios.get(
            `/offer/get-offer-for-buyer/${buyerId}?${params}`,
            { headers: { Authorization: `Bearer ${token}` } },
          );

          if (recommendedRes.data.success) {
            setRecommendedDeals(recommendedRes.data.data.Recommended_Deal_For_You ?? []);
            setTotals((prev) => ({
              ...prev,
              recommended: recommendedRes.data.data.total?.recommended ?? 0,
            }));
          }
        }
      } catch (err) {
        console.error("Failed to fetch offers:", err);
        toast.error("Failed to load offers");
      } finally {
        setLoading(false);
      }
    };

    fetchOffers();
  }, [buyerId, debouncedSearch, isLoggedIn, token]);

  // ── Fetch wallet (logged-in only) — needed for the pay-to-view modal ────
  useEffect(() => {
    if (!isLoggedIn || !buyerId) return;

    const fetchWallet = async () => {
      try {
        const { data } = await axios.get(
          `/wallet/get-wallet-balance/${buyerId}`,
        );
        setWallet(data.data);
      } catch (err) {
        console.error("Failed to fetch wallet:", err);
      }
    };

    fetchWallet();
  }, [isLoggedIn, buyerId]);

  // ── Click handler: navigate directly to post-order page where payment gate exists ─
  const handleOfferClick = (item) => {
    const offerId = item?.offer?._id;
    if (!offerId) return;
    navigate(`/post-order-via-offer?offerId=${offerId}`);
  };

  const handlePaid = (offerId) => {
    setPayModal({ show: false, offer: null });
    navigate(`/post-order-via-offer?offerId=${offerId}`);
  };

  const refreshWallet = async () => {
    if (!buyerId) return;
    try {
      const { data } = await axios.get(
        `/wallet/get-wallet-balance/${buyerId}`,
      );
      setWallet(data.data);
    } catch (err) {
      console.error("Failed to refresh wallet", err);
    }
  };

  // ── Paginated slice ────────────────────────────────────────────────────────
  const allItems = activeTab === "hot" ? hotDeals : recommendedDeals;
  const paginatedItems = allItems.slice(0, page * PAGE_SIZE);
  const hasMore = paginatedItems.length < allItems.length;

  const handleClearFilters = () => {
    setSearch("");
    setActiveTab("hot");
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* ── Sticky top bar ────────────────────────────────────────────────── */}
      <div className="sticky top-0 z-30 bg-white border-b border-gray-100 shadow-sm">
        <div className="2xl:max-w-7xl mx-auto px-4 md:px-8">
          {/* Header row */}
          <div className="flex items-center gap-3 py-4">
            <button
              onClick={() => navigate(-1)}
              className="w-9 h-9 flex items-center justify-center rounded-xl bg-gray-100 hover:bg-gray-200 transition flex-shrink-0"
            >
              <svg className="w-5 h-5 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <div className="flex-1">
              <h1 className="text-lg font-bold text-gray-900">All Offers</h1>
              <p className="text-xs text-gray-400 hidden sm:block">
                {activeTab === "hot" ? totals.hot : totals.recommended} offers available
              </p>
            </div>

            {/* Search — desktop */}
            <div className="relative hidden sm:flex items-center w-64 md:w-80">
              <FiSearch className="absolute left-3 text-gray-400 text-sm" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search variety, grade..."
                className="w-full pl-8 pr-8 py-2 text-sm rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primaryColor/20 focus:border-primaryColor/30 bg-gray-50"
              />
              {search && (
                <button onClick={() => setSearch("")} className="absolute right-2.5 text-gray-400 hover:text-gray-600">
                  <FiX size={14} />
                </button>
              )}
            </div>
          </div>

          {/* Search — mobile */}
          <div className="sm:hidden pb-3">
            <div className="relative flex items-center">
              <FiSearch className="absolute left-3 text-gray-400 text-sm" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search variety, grade..."
                className="w-full pl-8 pr-8 py-2.5 text-sm rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primaryColor/20 bg-gray-50"
              />
              {search && (
                <button onClick={() => setSearch("")} className="absolute right-2.5 text-gray-400 hover:text-gray-600">
                  <FiX size={14} />
                </button>
              )}
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-1 pb-0">
            {[
              { key: "hot", label: "Hot Deals", count: totals.hot },
              ...(isLoggedIn ? [{ key: "recommended", label: "Recommended", count: totals.recommended }] : []),
            ].map(({ key, label, count }) => (
              <button
                key={key}
                onClick={() => {
                  setActiveTab(key);
                  setSearchParams({ type: key });
                }}
                className={`
                  flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium
                  border-b-2 transition-all duration-150
                  ${activeTab === key
                    ? "border-primaryColor text-primaryColor"
                    : "border-transparent text-gray-500 hover:text-gray-700"
                  }
                `}
              >
                {label}
                <span className={`
                  text-[10px] font-bold px-1.5 py-0.5 rounded-full
                  ${activeTab === key ? "bg-primaryColor/10 text-primaryColor" : "bg-gray-100 text-gray-500"}
                `}>
                  {count}
                </span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ── Grid ──────────────────────────────────────────────────────────── */}
      <div className="2xl:max-w-7xl mx-auto px-4 md:px-8 pt-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 md:gap-4">
          {loading ? (
            [...Array(PAGE_SIZE)].map((_, i) => <SkeletonCard key={i} />)
          ) : paginatedItems.length === 0 ? (
            <EmptyState onClear={handleClearFilters} />
          ) : (
            paginatedItems.map((item, i) => (
              <OfferGridCard
                key={item?.offer?._id ?? i}
                item={item}
                onOfferClick={handleOfferClick}
              />
            ))
          )}
        </div>

        {/* Load more */}
        {!loading && hasMore && (
          <div className="flex justify-center mt-8">
            <button
              onClick={() => setPage((p) => p + 1)}
              className="
                flex items-center gap-2 px-8 py-3 rounded-full
                bg-white border-2 border-primaryColor text-primaryColor
                text-sm font-semibold
                hover:bg-primaryColor hover:text-white
                transition-all duration-200 active:scale-95 shadow-sm
              "
            >
              Load More
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
              </svg>
            </button>
          </div>
        )}

        {/* End of list message */}
        {!loading && !hasMore && allItems.length > 0 && (
          <p className="text-center text-xs text-gray-400 mt-8 pb-4">
            You've seen all {allItems.length} offer{allItems.length !== 1 ? "s" : ""}
          </p>
        )}
      </div>


      {/* Pay to view offer modal */}
      <PayToViewOfferModal
        show={payModal.show}
        offer={payModal.offer}
        wallet={wallet}
        user={storedUser}
        onClose={() => setPayModal({ show: false, offer: null })}
        onPaid={handlePaid}
      />
    </div>
  );
};

export default AllOffers;